import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowRight, RotateCcw, Award, Home } from 'lucide-react';
import Navbar from '../components/Navbar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SimulationPlayer() {
  const { id: simId } = useParams();
  const navigate = useNavigate();
  const [sim, setSim] = useState(null);
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pendingChoice, setPendingChoice] = useState({}); // {decisionId: choiceId | numeric}
  const [feedbackByDecision, setFeedbackByDecision] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/simulations/${simId}`);
        setSim(res.data);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Could not load simulation');
        navigate('/simulations');
      } finally {
        setLoading(false);
      }
    })();
  }, [simId, navigate]);

  const startRun = async () => {
    setStarting(true);
    try {
      const res = await axios.post(`${API}/simulations/${simId}/runs`);
      setRun(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start simulation');
    } finally {
      setStarting(false);
    }
  };

  const submitAllDecisions = async () => {
    if (!run || !sim) return;
    const currentRound = sim.rounds[run.current_round_index];
    if (!currentRound) return;
    // Validate every decision has a choice
    for (const d of currentRound.decisions) {
      if (!(d.id in pendingChoice)) {
        toast.error(`Please respond to: "${d.question}"`);
        return;
      }
    }
    setSubmitting(true);
    let latestState = run.variables_state;
    const newFeedback = { ...feedbackByDecision };
    try {
      for (const d of currentRound.decisions) {
        const val = pendingChoice[d.id];
        const body = { round_id: currentRound.id, decision_id: d.id };
        if (d.decision_type === 'multiple_choice') {
          body.choice_id = val;
          const chosen = d.choices.find(c => c.id === val);
          if (chosen?.feedback) newFeedback[d.id] = chosen.feedback;
        } else {
          body.numeric_value = Number(val);
        }
        const res = await axios.post(`${API}/runs/${run.id}/decision`, body);
        latestState = res.data.variables_state;
      }
      // Advance
      const adv = await axios.post(`${API}/runs/${run.id}/advance`);
      if (adv.data.status === 'completed') {
        setRun(prev => ({
          ...prev,
          status: 'completed',
          variables_state: latestState,
          final_score: adv.data.final_score,
          score_breakdown: adv.data.score_breakdown,
        }));
      } else {
        setRun(prev => ({
          ...prev,
          variables_state: latestState,
          current_round_index: adv.data.current_round_index,
        }));
      }
      setFeedbackByDecision(newFeedback);
      setPendingChoice({});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not submit decision');
    } finally {
      setSubmitting(false);
    }
  };

  const restart = () => {
    setRun(null);
    setPendingChoice({});
    setFeedbackByDecision({});
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-900" /></div>;
  if (!sim) return null;

  // Pre-start briefing screen
  if (!run) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="pt-24 pb-16 px-6" data-testid="sim-brief-screen">
          <div className="max-w-3xl mx-auto bg-white rounded-xl border border-slate-200 p-8">
            <div className="flex items-center gap-2 mb-3">
              <span className="px-2.5 py-0.5 bg-orange-100 text-orange-700 text-xs font-mono rounded uppercase">{sim.template_type?.replace('_', ' ')}</span>
              {sim.subject && <span className="px-2.5 py-0.5 bg-slate-100 text-slate-700 text-xs font-mono rounded">{sim.subject}</span>}
            </div>
            <h1 className="text-3xl font-serif font-bold text-slate-900 mb-3">{sim.title}</h1>
            <p className="text-slate-600 mb-6">{sim.description}</p>
            {sim.learner_role && (
              <div className="mb-4">
                <div className="text-xs uppercase font-mono text-slate-500 mb-1">Your Role</div>
                <div className="text-slate-800 font-medium">{sim.learner_role}</div>
              </div>
            )}
            {sim.learning_objectives?.length > 0 && (
              <div className="mb-6">
                <div className="text-xs uppercase font-mono text-slate-500 mb-2">Learning Objectives</div>
                <ul className="list-disc list-inside text-slate-700 text-sm space-y-1">
                  {sim.learning_objectives.map((o, i) => <li key={i}>{o}</li>)}
                </ul>
              </div>
            )}
            <div className="text-xs text-slate-500 mb-6">Estimated duration: {sim.duration_minutes} minutes • {sim.rounds?.length || 0} rounds • Difficulty: <span className="capitalize">{sim.difficulty}</span></div>
            <button onClick={startRun} disabled={starting} className="inline-flex items-center gap-2 px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50" data-testid="sim-start-button">
              {starting ? 'Starting...' : 'Start simulation'} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Completed screen
  if (run.status === 'completed') {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="pt-24 pb-16 px-6" data-testid="sim-completed-screen">
          <div className="max-w-3xl mx-auto bg-white rounded-xl border border-slate-200 p-8">
            <div className="text-center mb-6">
              <Award className="w-14 h-14 text-orange-600 mx-auto mb-3" />
              <h1 className="text-3xl font-serif font-bold text-slate-900 mb-1">Simulation Complete</h1>
              <p className="text-slate-600">{sim.title}</p>
            </div>
            <div className="text-center mb-8">
              <div className="text-xs uppercase font-mono text-slate-500 mb-1">Final Score</div>
              <div className="text-6xl font-serif font-bold text-orange-600" data-testid="sim-final-score">{run.final_score}</div>
              <div className="text-sm text-slate-500">out of 100</div>
            </div>
            {Object.keys(run.score_breakdown || {}).length > 0 && (
              <div className="mb-8">
                <div className="text-xs uppercase font-mono text-slate-500 mb-3">Breakdown</div>
                <div className="space-y-2">
                  {Object.entries(run.score_breakdown).map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <span className="text-slate-700 font-medium">{k}</span>
                      <span className="text-slate-900 font-mono">{v}/100</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {sim.debrief && (
              <div className="mb-8">
                <div className="text-xs uppercase font-mono text-slate-500 mb-2">Debrief</div>
                <div className="prose prose-slate max-w-none text-slate-700 whitespace-pre-line">{sim.debrief}</div>
              </div>
            )}
            <div className="flex gap-3">
              <button onClick={restart} className="inline-flex items-center gap-2 px-5 py-2.5 border border-slate-300 rounded-lg hover:bg-slate-50 text-sm" data-testid="sim-restart-button"><RotateCcw className="w-4 h-4" /> Play again</button>
              <Link to="/simulations" className="inline-flex items-center gap-2 px-5 py-2.5 border border-slate-300 rounded-lg hover:bg-slate-50 text-sm"><Home className="w-4 h-4" /> More simulations</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // In-progress
  const currentRound = sim.rounds[run.current_round_index];
  const totalRounds = sim.rounds.length;
  const visibleVars = sim.variables.filter(v => v.visible !== false);

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="pt-24 pb-16 px-6" data-testid="sim-play-screen">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs uppercase font-mono text-slate-500">Round {run.current_round_index + 1} of {totalRounds}</div>
              <h1 className="text-2xl font-serif font-bold text-slate-900" data-testid="sim-round-title">{currentRound?.title || `Round ${run.current_round_index + 1}`}</h1>
            </div>
            <span className="text-xs text-slate-500">{sim.title}</span>
          </div>

          {/* Dashboard */}
          {visibleVars.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="sim-dashboard">
              {visibleVars.map(v => (
                <div key={v.name} className="bg-white border border-slate-200 rounded-lg p-4">
                  <div className="text-xs uppercase font-mono text-slate-500 mb-1">{v.name}</div>
                  <div className="text-2xl font-serif font-bold text-slate-900">
                    {Number(run.variables_state?.[v.name] ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    {v.unit && <span className="text-sm ml-1 text-slate-500">{v.unit}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Scenario */}
          {currentRound?.scenario && (
            <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6" data-testid="sim-scenario">
              <div className="text-xs uppercase font-mono text-slate-500 mb-2">Scenario</div>
              <div className="prose prose-slate max-w-none text-slate-700 whitespace-pre-line">{currentRound.scenario}</div>
            </div>
          )}

          {/* Decisions */}
          <div className="space-y-4">
            {currentRound?.decisions.map((d, di) => (
              <div key={d.id} className="bg-white border border-slate-200 rounded-xl p-6" data-testid={`sim-decision-${d.id}`}>
                <div className="text-xs uppercase font-mono text-slate-500 mb-1">Decision {di + 1}</div>
                <h3 className="text-lg font-serif font-semibold text-slate-900 mb-4">{d.question}</h3>
                {d.decision_type === 'multiple_choice' ? (
                  <div className="space-y-2">
                    {d.choices.map(c => (
                      <label key={c.id} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${pendingChoice[d.id] === c.id ? 'border-orange-600 bg-orange-50' : 'border-slate-200 hover:border-slate-400'}`}>
                        <input type="radio" name={`d-${d.id}`} checked={pendingChoice[d.id] === c.id} onChange={() => setPendingChoice(p => ({ ...p, [d.id]: c.id }))} className="mt-1" data-testid={`sim-choice-${c.id}`} />
                        <span className="text-sm text-slate-800">{c.label}</span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-3">
                    <input
                      type="number"
                      min={d.min_value ?? undefined}
                      max={d.max_value ?? undefined}
                      value={pendingChoice[d.id] ?? d.default_value ?? ''}
                      onChange={(e) => setPendingChoice(p => ({ ...p, [d.id]: e.target.value }))}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                      placeholder={d.min_value != null && d.max_value != null ? `${d.min_value} - ${d.max_value}` : 'Enter a number'}
                      data-testid={`sim-numeric-${d.id}`}
                    />
                    <div className="text-xs text-slate-500">Range: {d.min_value ?? '—'} to {d.max_value ?? '—'}</div>
                  </div>
                )}
                {feedbackByDecision[d.id] && (
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded text-sm text-blue-900">{feedbackByDecision[d.id]}</div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-6 flex justify-end">
            <button onClick={submitAllDecisions} disabled={submitting} className="inline-flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-lg hover:bg-slate-800 disabled:opacity-50" data-testid="sim-submit-round-button">
              {submitting ? 'Submitting...' : (run.current_round_index + 1 >= totalRounds ? 'Complete simulation' : 'Submit & continue')} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
